import Styles from './BrandAICard.module.scss'
import { useFocusRing } from '@react-aria/focus'
import cx from 'classnames'
import { mergeProps } from '@react-aria/utils'
import { Paragraph } from 'blaze-ui/Paragraph/Paragraph'
import {
  BaseBrandAICardProps,
  BrandAICardLinkProps,
  BrandAICardProps,
  PressableBrandAICardProps,
} from 'screens/BrandKits/components/BrandAICard/Types'
import { forwardRef } from 'react'
import { Pressable } from '@react-aria/interactions'
import { useCarouselPress } from 'components/Carousel/useCarouselPress'
import { Link } from 'react-router-dom'
import { Text } from 'blaze-ui/Text/Text'

export const ReadOnlyBrandAICard = forwardRef<HTMLDivElement, BaseBrandAICardProps>(
  ({ title, preview: Preview, icon, ...props }, fRef) => {
    return (
      <div ref={fRef} className={cx(Styles.root, Styles.readOnly)} {...props}>
        <div className={Styles.preview}>
          <Preview />
        </div>
        {title && (
          <div className={cx(Styles.title, { [Styles.withIcon]: !!icon })}>
            {icon && <div className={Styles.icon}>{icon}</div>}
            <Paragraph variant="smallList" color="var(--dark-80)" lineClamp={2}>
              {title}
            </Paragraph>
          </div>
        )}
      </div>
    )
  },
)

export const BrandAICard = forwardRef<HTMLDivElement, BrandAICardProps>(
  (
    {
      title,
      preview: Preview,
      controls: Controls,
      isPressed = false,
      isFocusVisible,
      size = 'sm',
      lineClamp = 2,
      isSelected,
      withShadow = false,
      icon,
      ...props
    },
    fRef,
  ) => {
    return (
      <div
        ref={fRef}
        className={cx(Styles.root, Styles[size], {
          [Styles.focusVisible]: isFocusVisible || isSelected,
          [Styles.pressed]: isPressed,
        })}
        aria-label={title}
        tabIndex={0}
        role="button"
        {...props}
      >
        <div className={cx(Styles.preview, { [Styles.withShadow]: withShadow })}>
          <Preview />
          <div className={Styles.controls}>{Controls && <Controls />}</div>
        </div>
        {title && (
          <div className={cx(Styles.title, { [Styles.withIcon]: !!icon })}>
            {icon && <div className={Styles.icon}>{icon}</div>}
            <Text variant="smallList" color="var(--dark-80)" lineClamp={lineClamp}>
              {title}
            </Text>
          </div>
        )}
      </div>
    )
  },
)

export const PressableBrandAICard = ({
  title,
  icon,
  preview,
  controls,
  size,
  lineClamp,
  withShadow,
  ...props
}: PressableBrandAICardProps) => {
  const { isPressed, pressProps, onTouchStart, onTouchMove, onTouchEnd } = useCarouselPress(props)
  const { isFocusVisible, focusProps } = useFocusRing(props)

  return (
    <Pressable {...pressProps}>
      <BrandAICard
        title={title}
        icon={icon}
        preview={preview}
        controls={controls}
        size={size}
        lineClamp={lineClamp}
        withShadow={withShadow}
        isPressed={isPressed}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        isFocusVisible={isFocusVisible}
        {...focusProps}
      />
    </Pressable>
  )
}

export const BrandAICardLink = ({
  title,
  to,
  replace,
  component,
  preview,
  controls,
  ...props
}: BrandAICardLinkProps) => {
  const { pressProps, isPressed, onTouchEnd, onTouchMove, onTouchStart } = useCarouselPress(props)
  const { isFocusVisible, focusProps } = useFocusRing(props)

  return (
    <Link
      to={to}
      replace={replace}
      component={component}
      className={Styles.link}
      aria-label={title}
      {...mergeProps(pressProps, focusProps)}
    >
      <BrandAICard
        title={title}
        preview={preview}
        controls={controls}
        {...props}
        isFocusVisible={isFocusVisible}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        isPressed={isPressed}
      />
    </Link>
  )
}
